import { Avatar } from './Avatar.js';
import { VideoTile } from './VideoTile.js';
import type { StagePerson } from '../lib/useRoom.js';

/**
 * The stage.
 *
 * Everyone here can speak — listeners are `hidden` at the SFU and never reach
 * the client at all. The grid is centred and grows from the middle so a
 * two-person show does not sit awkwardly in a corner, and tiles keep a stable
 * order (see useRoom) so nobody jumps around mid-sentence.
 *
 * Audio and video shows share this grid rather than branching into two screens:
 * a room can be video-enabled while nobody has switched a camera on yet, and
 * cameras go on and off mid-show. So the tile shape follows the TRACK, not the
 * room setting, and a show degrades to faces without a layout change.
 */
export function StageGrid({ people, hostSubs }: { people: StagePerson[]; hostSubs: string[] }) {
  // Once anyone is on camera the grid switches to wide tiles: mixing a 16:9
  // video with round avatars at the same width makes the video tiny.
  const hasVideo = people.some((person) => person.videoTrack);

  return (
    <ul class={`stage${hasVideo ? ' stage--video' : ''}`} role="list">
      {people.map((person) => {
        const muted = !person.hasMic || person.isMuted;
        const isHost = hostSubs.includes(person.identity);

        if (person.videoTrack) {
          return (
            <li key={person.identity} class="stage__slot stage__slot--video">
              <VideoTile person={person} />
              <p class="stage__role">
                {isHost ? <span class="chip chip--host">Host</span> : null}
                <span class={person.isSpeaking ? 'stage__live' : undefined}>
                  {person.isSpeaking ? 'Speaking' : muted ? 'Muted' : 'On camera'}
                </span>
              </p>
            </li>
          );
        }

        return (
          <li key={person.identity} class="stage__slot">
            <Avatar
              sub={person.identity}
              name={person.name}
              size="lg"
              level={person.level}
              speaking={person.isSpeaking}
              muted={muted}
            />
            <p class="stage__name" title={person.name}>
              {person.name}
              {person.isLocal ? <span class="stage__you"> · you</span> : null}
            </p>
            <p class="stage__role">
              {isHost ? <span class="chip chip--host">Host</span> : null}
              <span class={person.isSpeaking ? 'stage__live' : undefined}>
                {person.isSpeaking ? 'Speaking' : muted ? 'Muted' : 'On stage'}
              </span>
            </p>
          </li>
        );
      })}
    </ul>
  );
}
